import { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import type { ActionPlan, PlanArea } from '../../../api/workEnvironmentTypes';
import { areaNames } from '../planView';
import { SavedText } from '../components/FormParts';
import { dayAndTime } from '../riskView';
import { dayAndMonth } from '../surveyProcessView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';
import { longDate } from '../../Settings/privacyView';

type Props = { plan: ActionPlan; areas: PlanArea[]; onBack: () => void; children?: ReactNode };

/** A plan that is not being edited, below what is happening with its approval */
export const PlanSummary = ({ plan, areas, onBack, children }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const many = plan.areas.length > 1;
  const namesOf = (factorKeys: string[]) => areaNames(areas.filter((area) => factorKeys.includes(area.key)), t);
  return (
    <div className={classes.mainColumn}>
      <div>
        <Button type="button" variant="tertiary" data-size="sm" onClick={onBack}>
          <ArrowLeftIcon aria-hidden />
          {t('workEnvironmentPlan.builder.back')}
        </Button>
      </div>
      {children}
      <section aria-labelledby="plan-summary-heading" className={process.card}>
        <div className={process.cardBody}>
          <Heading level={2} data-size="sm" id="plan-summary-heading">
            {t('workEnvironmentPlan.builder.measuresTitle')}
          </Heading>
          <ol className={classes.dataAreas}>
            {plan.measures.map((measure) => (
              <li key={measure.id} className={classes.dataArea}>
                <span className="ds-label">{measure.title}</span>
                {measure.description && <Paragraph data-size="sm">{measure.description}</Paragraph>}
                <Paragraph data-size="xs" className={process.subtle}>
                  {t(many ? 'workEnvironmentPlan.summary.measureMetaAreas' : 'workEnvironmentPlan.summary.measureMeta', {
                    owner: measure.owner ?? t('workEnvironmentPlan.builder.noOwner'),
                    date: dayAndMonth(measure.dueDate, i18n.language),
                    areas: namesOf(measure.factorKeys),
                  })}
                </Paragraph>
              </li>
            ))}
          </ol>
          {plan.areas.map(
            ({ factorKey, goal }) =>
              goal && (
                <SavedText
                  key={factorKey}
                  title={
                    many
                      ? t('workEnvironmentPlan.builder.goalFor', { name: namesOf([factorKey]) })
                      : t('workEnvironmentPlan.builder.goal')
                  }
                  text={goal}
                />
              ),
          )}
          {plan.frequency && (
            <SavedText
              title={t('workEnvironmentPlan.builder.frequency')}
              text={t(`workEnvironmentPlan.builder.frequencies.${plan.frequency}`)}
            />
          )}
          {plan.evaluationDate && (
            <SavedText
              title={t('workEnvironmentPlan.builder.evaluationDate')}
              text={longDate(plan.evaluationDate, i18n.language)}
            />
          )}
          {plan.approved && (
            <Paragraph data-size="sm" className={process.subtle}>
              {t(plan.approved.by ? 'workEnvironmentPlan.summary.approvedBy' : 'workEnvironmentPlan.summary.approved', {
                email: plan.approved.by,
                when: dayAndTime(plan.approved.at, t, i18n.language),
              })}
            </Paragraph>
          )}
        </div>
      </section>
    </div>
  );
};
